import $ from "jquery"
import arrayToTree from "array-to-tree"
import bootstrap from "bootstrap.native"
import camelCase from "camelcase"
import * as Sentry from "@sentry/browser"
import clone from "clone"
import Combobox from "../interface/combobox.class"
import download from "../util/download"
import equal from "deep-equal"
import formatter from "./formatter"
import Hook from "../hook"
import notificationTemplate from "../../views/snippet/notification.html"
import serializeForm from "../util/serializeForm"
import shortid from "shortid"
import swal from "sweetalert"
import templater from "../interface/templater"

const {drawVueManualModal} = require("../interface/vueManualModal")
const {notify} = require("../util/notify")

/**
 * Window object
 * @property {string} id - Window Identifier
 * @property {Window} parent - Parent window
 * @property {Object} sub - Sub window object
 * @property {Session} session - Current session
 * @property {boolean} loading - If the window is loading
 * @property {Element} element - window html element
 * @property {Object} input - Server Input object
 * @property {Object} output - Server output object
 * @property {Object} output.Request - Request object
 * @property {Object} output.Data - Data object
 * @property {number} counter - counter
 * @property {Object} lastSuccessInput - Last succesful input values
 * @property {Object} actionInputArgs - (Dialogue)Action input
 * @property {*} lastActionArgs - Last succesful action input values
 * @property {boolean} waitingForReload - Windows is waiting for reload
 * @property {*} error - Window error
 * @property {boolean} empty - Window is empty
 * @property {Object} options - Window logic options
 * @property {boolean} options.customKeyEvents - Ignore normal key events like escape, enter etc.
 * @property {boolean} options.noNewLines - Do not add new line to bulkedit
 * @property {boolean} options.autoFocus - Autofocus first input
 * @property {boolean} options.noReset - Allow reset
 * @property {Object} renderOptions - Window render options
 * @property {boolean} renderOptions.actionbar - Show action bar
 * @property {boolean} renderOptions.customButtons - Use custom buttons in bulkedit mode
 * @property {boolean} renderOptions.overlay - Cover the whole form(also cover subforms)
 * @property {boolean} renderOptions.inactive - Cover content
 * @property {Object} filters - Window filters
 * @property {*} bulkedit - Window is in bulkedit mode
 * @property {Array} selection - List of selection
 * @property {Array} tabs - List of tabs
 * @property {string | null} customTemplateName - (optional) Custom template name
 * @property {string | null} customTitle - (optional) Custom title
 * @property {Object} customData - (optional) custom Data
 * @property {boolean} customInit - (optional) custum Init function check
 */
export default class Window {
  id = null
  element = null
  parent = null
  sub = null
  session = null
  loading = true

  input = {}

  output = {
    Request: {},
    Data: {},
  }

  previousPostbackValues = null

  counter = 0
  lastSuccessInput = null
  actionInputArgs = {}
  lastActionArgs = null
  waitingForReload = false
  error = null
  empty = false

  options = {
    customKeyEvents: false,
    noNewLines: false,
    autoFocus: true,
    noReset: false,
  }

  renderOptions = {
    actionbar: true,
    customButtons: false,
    overlay: false,
    inactive: false,
  }

  // table properties
  filters = {}

  bulkedit = false
  selection = []
  tabs = []

  // custom variables
  customTemplateName = null
  customTitle = null
  customData = {}
  customInit = false

  // Initiate modal instances
  extraLargeModalInstance = new bootstrap.Modal(extraLargeModal, {
    content: "<p></p>",
    backdrop: "static",
  })

  smallModalInstance = new bootstrap.Modal(smallModal, {
    content: "<p></p>",
    backdrop: "static",
  })

  /**
   * Window constructor
   * @param {Session} session - Session
   * @param {Window} parent - Parent window
   */
  constructor(session, parent = null) {
    this.parent = parent || null
    this.session = session
    this.moment = require("moment")
    this.moment.locale(global.session.language)

    // set ID
    this.id = shortid.generate()

    // Register window in session and run create event
    if (this.session !== null) {
      this.session.windows[this.id] = this
      this.setDefaultFilters()
      Hook.run("create", this)
    }

    /*if (this.parent) {
			// because subwindows are hard to reset
			this.options.noReset = true
		}*/

    return this
  }

  /**
   * Set default filters
   * @returns {void}
   */
  setDefaultFilters() {
    this.filters = {
      usedate: false,
      date: [
        {
          text: this.session.translations.FilterDateStart,
          suffix: ">=",
          value: null,
        },
        {
          text: this.session.translations.FilterDateEnd,
          suffix: "<=",
          value: null,
        },
      ],
      other: [],
      selectedOthers: 0,
    }
  }

  /**
   * Emit event
   * @deprecated
   * @param {*} eventName - Event name
   * @param {Array} args - Arguments
   * @returns {Promise} Promise
   */
  async emit(eventName, ...args) {
    try {
      // await this.session.hooks.run(eventName, this, { args })
      Hook.run(eventName, this, null, {args})
    } catch (err) {
      this.setError(err)
    }
  }

  /**
   * set and render Window error
   * @param {string} err - Error
   * @returns {void}
   */
  setError(err) {
    console.error(err)
    this.empty = false
    this.loading = false
    this.error = err
    this.render()
  }

  /**
   * Load default options, output values, filters, options and renderOptions
   * @param {Array} ignore - Properties that have to be ignored
   * @returns {void}
   * @deprecated
   */
  loadDefaultValues(...ignore) {
    let template = new Window(this.session)

    for (let i of ["input", "output", "filters", "renderOptions", "options"]) {
      if (ignore.indexOf(i) === -1) {
        this[i] = clone(template[i])
      }
    }
  }

  /**
   * Run event
   * @param {string} eventName - Event name
   * @param {Array} args - Arguments
   * @returns {Promise} Return a promise, maybe
   */
  async event(eventName, ...args) {
    let event = camelCase(eventName)
    try {
      // check if the event is a function
      if (typeof this[event] !== "function") {
        throw new Error("Unknown function '" + event + "'")
      }

      // get what is hopefully a promise
      let maybePromise = this[event].call(this, ...args)

      // check if it is a promise
      if (maybePromise && typeof maybePromise.then === "function") {
        // then run it, with loading indication
        this.toggleLoading(true)
        maybePromise = await Promise.resolve(maybePromise)
        this.toggleLoading(false)
      }

      return maybePromise
    } catch (err) {
      console.error(event, err)
      this.toggleLoading(false)
      $("body").removeClass("avoid-clicks")
      this.message("error", err.message || "An error occurred")
      throw err
    }
  }

  /**
   * Close and remove window object
   * @see {@link Canvas}
   * @see {@link Session}
   * @param {boolean} shouldConfirm - If confirmation is needed to close the window
   * @param {boolean} keepTabSelection - If the window is being reloaded
   * @returns {void}
   */
  dispose(shouldConfirm = true, keepTabSelection = false) {
    // Run hookable event
    Hook.run("dispose", this, async (e) => {
      try {
        // check if we need to show a confirmation
        let isDirty =
          this.isDirty() ||
          (this.sub !== null &&
            this.sub.window !== null &&
            this.sub.window.isDirty())

        let alertObject = {
          text: this.session.translations.WindowWarningOnClose,
          icon: "warning",
          dangerMode: true, // setting dangerMode to true makes the Cancel button bigger
          buttons: [this.session.translations.Cancel, true],
        }

        // if the window is dirty and we should confirm and the user has cancelled the close dialog, cancel the dispose action.
        // cancelling the alert resolves the promise to NULL, whereas clicking OK resolves to true
        if (isDirty && shouldConfirm && !(await swal(alertObject))) {
          e.cancel = true
          return
        }
      } catch (error) {
        console.error(error)
      }

      // tell parent their sub window has been closed
      if (this.parent) {
        delete this.parent.sub.window
        this.parent.sub = {dummy: true}
        this.parent.loadSub()

        if (!keepTabSelection) {
          this.parent.tabs.forEach((t) => {
            t.Selected = false
          })
        }

        // this.parent.clearHTML()
        this.clearHTML()
        await this.parent.render()
        // this.parent.sub.window.render()
        return
      }

      // remove combobox references
      this.removeComboboxReferences()

      // delete reference from session
      delete this.session.windows[this.id]

      // remove self from session tab
      let index = this.session.tabs.indexOf(this)
      if (index > -1) {
        this.session.tabs.splice(index, 1)
      }

      // If we are the active window
      if (this.session.activeWindow === this) {
        // Focus a new window
        this.session.activeWindow = null

        let newIndex = Math.max(index - 1, 0)
        let newWindow = this.session.tabs[newIndex]

        if (newWindow) {
          newWindow.focus()
        }
      }
    })
  }

  /**
   * Disposes all windows
   * @returns {void}
   */
  disposeAll() {
    let i
    let tabAmount = this.session.tabs.length
    for (i = 0; i < tabAmount; i++) {
      // Take the first tab in of the array.
      // Because after an delete in an array the next value moves from index number.
      let tab = this.session.tabs[0]
      tab.dispose(false)
    }
  }

  /**
   * Remove combobox references
   * @returns {void}
   */
  removeComboboxReferences() {
    // If there are rows
    if (
      this.output &&
      this.output.Table &&
      this.output.Table.Rows &&
      this.output.Table.Rows.length
    ) {
      this.output.Table.Rows.forEach((row) => {
        // remove each row's combobox
        row.forEach((column) => {
          if (column.Combobox) {
            column.Combobox.remove()
          }
        })
      })
    }
  }

  /**
   * Get window title
   * @param {boolean} bare - Append counter to string
   * @returns {string} Window title
   */
  title(bare = false) {
    return (
      (this.customTitle ||
        this.output.Title ||
        this.session.translations.LoadingWindow) +
      (!bare && this.counter !== 0 && !this.session.kiosk
        ? " (" + this.counter + ")"
        : "")
    )
  }

  /**
   * Set new window title
   * @param {string} title - New custom window title
   * @returns {Window} returns self
   */
  setTitle(title) {
    this.customTitle = title
    return this
  }

  /**
   * Set counter
   * @todo Document. What is this?
   * @returns {void}
   */
  setCounter() {
    if (this.parent !== null) {
      return
    }

    let base = this.title()
    let counters = []

    for (let window of this.session.tabs) {
      if (window === this) {
        continue
      }

      if (window.title(true) === base) {
        counters.push(window.counter)
      }
    }

    while (counters.indexOf(this.counter) !== -1) {
      this.counter++
    }
  }

  /**
   * Open table row in new window
   * @param {string} id - Row id
   * @param {string} fieldIndex - Optional field (column) index (when a specific linked column is clicked)
   * @param {string} subject - Provide your subject manually
   * @param {string} key - Key to be provided manually when not using a standard table
   * @param {string} keyReference - KeyReference to be provided manually when not using a standard table
   * @returns {void}
   */
  openRow(id, fieldIndex, subject, key, keyReference) {
    // run hookable function
    // Set input of new window
    let input = {}
    let column = null
    input.Subject = this.input.Subject
    input.Prefix = "Single"
    input.Criteria = {}

    if (subject) {
      input.Subject = subject
    }

    if (fieldIndex) {
      column = this.output.Table.Columns[fieldIndex]
    }

    if (keyReference) {
      // if key is provided manually (when not using a standard table) create criteria from key
      input.Criteria = [{[keyReference]: key}]
    } else if (column && column.Dropdown && column.Dropdown.OpenRef) {
      // if fieldIndex is not null and fieldIndex has a reference to another table, open that table
      input.Subject = column.Dropdown.TableName
      input.Criteria = [
        {
          [column.Dropdown.ColumnName]: this.output.FullTable.Rows[id].find(
            (cell) => {
              return cell.Column.Name === column.Name
            },
          ).Value,
        },
      ]
    } else if (this.output.FullTable.Rows[id]) {
      // build criteria based on the selected rowID
      input.Criteria = [this.buildCriteriaNew(this.output.FullTable.Rows[id])]
    }

    // temporaraly store the clicked rowID in input.rowID for hooks
    input.rowID = id

    return Hook.run(
      "openrow",
      this,
      async () => {
        // remove temporariy rowID
        delete input.rowID

        // Create the new window
        await this.session.openWindow(input)
      },
      input,
    )
  }

  /**
   * Process server output to be used with window
   * @param {Object} output - Server output
   * @returns {Promise} Promise
   */
  async process(output) {
    $("body").addClass("avoid-clicks")
    try {
      await Hook.run("process", this, async () => {
        // go to table view, e. g. no bulk edit
        this.bulkedit = false
        this.output = output

        // process data
        formatter.format(this)

        // set input to what was send to server(because some hook might have edited)
        if (this.output.Request !== null) {
          // Always use criteria as selection for Form's (single's or custom prefixes)
          if (
            this.output.Data.Type == "form" &&
            this.output.Request.Prefix != "New"
          ) {
            this.selection = this.output.Request.Criteria
          } else {
            this.selection = []
          }

          this.input = clone(this.output.Request)
        }

        // remove __type property
        if (this.output.Request.__type) {
          delete this.output.Request.__type
        }

        // set tabs
        this.tabs = this.output.Tabs || []
        this.tabs.forEach((tab) => {
          // event = "action:[ActionName]:[TableName]:[WebService]:[WebMethod]"
          tab.Event =
            "action:" +
            ["ActionName", "TableName", "WebService", "WebMethod"]
              .map(
                (i) =>
                  // Property or empty string
                  tab[i] || String(),
              )
              .join(":")
        })

        // set sub window
        if (this.output.Sub !== null) {
          await this.injectSub(this.output.Sub, true)
        }

        // if there is no default form to open but there are tabs we use a dummy window.
        if (this.tabs.length && !this.sub) {
          this.sub = {
            dummy: true,
          }
        }

        // keep inactive
        if (this.parent && this.parent.isDirty()) {
          this.renderOptions.inactive = true
        }

        await this.loadSub()

        this.setCounter()
        // TODO: Added because toggleLoading was not disabled after bulk-edit of existing orders. - 1-9-2017
        // this.toggleLoading(false)
      })
    } catch (error) {
      throw error
    }
    $("body").removeClass("avoid-clicks")
  }

  showHint(dataFieldIndex) {
    if (this.introJsInstance == null) {
      this.introJsInstance = global.introjs(this.element)
      this.introJsInstance.setOptions({hintPosition: "top-left"})

      // remove the old hints from the DOM-tree, preventing that hints will appear in the wrong tab
      // $("*[data-hint]").remove()

      this.introJsInstance.addHints()
    }

    let hint = this.introJsInstance._introItems.find(
      (x) =>
        (x.targetElement != null &&
          x.targetElement.dataset.fieldIndex == dataFieldIndex) ||
        (x.element.dataset != null &&
          x.element.dataset.fieldIndex == dataFieldIndex),
    )

    if (hint != null) {
      let indexOfHint = this.introJsInstance._introItems.indexOf(hint)

      if (indexOfHint > -1) {
        this.introJsInstance.showHint(indexOfHint)
        this.introJsInstance.showHintDialog(indexOfHint)
      }
    }
  }

  /**
   * Signal session that there may be inconsistant data(race conditions)
   * @see {@link Session.updateRelatedWindows}
   * @returns {void}
   */
  reloadRelated(table) {
    $("body").addClass("avoid-clicks")
    this.session.updateRelatedWindows(this, table)
    $("body").removeClass("avoid-clicks")
  }

  /**
   * Reload with new data
   * @param {boolean} keepSelection - If window should keep selection
   * @returns {Object} fetched data
   */
  async reload(keepSelection = false) {
    $("body").addClass("avoid-clicks")
    if (!keepSelection) {
      // Always use criteria as selection for Form's (single's or custom prefixes)
      if (
        this.output.Data.Type == "form" &&
        this.output.Request.Prefix != "New"
      ) {
        this.selection = this.output.Request.Criteria
      } else {
        this.selection = []
      }
    }

    // not useable with bulk edit
    this.bulkedit = false

    // get new data
    let result = await this.fetch()
    $("body").removeClass("avoid-clicks")
    return result
  }

  async reloadParent(keepSelection = false) {
    if (this.parent) {
      this.parent.reload(keepSelection)
    }
  }

  async confirm(text, responseOnConfirm, responseOnCancel) {
    try {
      let confirmationObject = {
        text: text,
        icon: "warning",
        dangerMode: true, // setting dangerMode to true makes the Cancel button bigger
        buttons: [this.session.translations.Cancel, true],
      }

      if (!(await swal(confirmationObject))) {
        if (responseOnCancel) {
          this.handleActionResponse(responseOnCancel)
        }
        return
      }
    } catch (error) {
      console.error(error)
    }

    if (responseOnConfirm) {
      this.handleActionResponse(responseOnConfirm)
    }
  }

  /**
   * Reset window completely
   * @param {string} shouldConfirm - If window should ask for comfirmation before closing
   * @param {boolean} shouldFocus - Should this window be focused?
   * @returns {Object} newly fetched data
   */
  async reset(shouldConfirm = null, shouldFocus = true, restoreWindowObject = {}) {
    // check if a closing confimation should be asked for
    if (
      shouldConfirm !== "skipconfirm" &&
      (shouldConfirm === "confirm" || this.isDirty()) &&
      !confirm(this.session.translations.WindowWarningOnClose)
    ) {
      return
    }

    if ($(this.element).find('.refreshWindowData').length > 0) {
      $(this.element).find('.refreshWindowData').get(0).click()
    }

    if (this.parent) {
      await this.parent.toggleLoading(true)

      this.dispose(false, true)

      let newWindow = this.session.newWindow(this.input)
      newWindow.parent = this.parent
      this.parent.sub = {
        window: newWindow,
      }

      await this.parent.toggleLoading(false)
      await this.parent.render()
      await newWindow.render()
      await newWindow.reload()
    } else {
      let index = this.session.tabs.indexOf(this)
      let customTitle = null

      // For multi, keep title, in case a custom title was used
      if (this.input.Prefix == "Multi") {
        customTitle = this.title()
      }

      this.dispose(false)
      let newWindow = this.session.newWindow(this.input, null, customTitle)

      newWindow.restoreWindowObject = restoreWindowObject

      this.session.tabs.splice(index, 0, newWindow)
      if (shouldFocus) {
        newWindow.focus()
      }

      newWindow.reload()
    }
  }

  /**
   * Fetch data from server, process and render window
   * @returns {Promise} Promise
   */
  async fetch() {
    $("body").addClass("avoid-clicks")
    // if there is no input, we cannot get data from server, exit
    if (!this.input) {
      this.loading = false
      $("body").removeClass("avoid-clicks")
      return await this.render()
    }

    // reset error and set loading
    this.loading = true
    this.error = null
    this.render()

    return await Hook.run("fetch", this, async () => {
      // send request and process output
      let url = "/Admin/WebServices/CoreWebServices.asmx/OpenWindow"
      this.output = await this.session.request(url, {request: this.input})

      await this.process(this.output)

      // store values for later
      this.lastSuccessInput = clone(this.input)

      // we are done loading, render output
      this.loading = false
      await this.render()
      drawVueManualModal({
        windowID: this.id,
        helpUrl: this.output.Options.HelpUrl,
      })
      $("body").removeClass("avoid-clicks")
    })
  }

  /**
   * Load sub window
   * @returns {Promise} Promise
   */
  async loadSub() {
    // we cannot load sub window we do not have
    if (!this.sub) {
      return
    }

    $("body").addClass("avoid-clicks")
    // load sub
    if (this.sub.dummy) {
      let child = new Window(this.session, this)
      this.sub.window = child
      child.empty = true
    }
    $("body").removeClass("avoid-clicks")
  }

  /**
   * Dummy method, hooked by canvas
   * @see {@link Canvas}
   * @returns {Promise} Promise
   */
  async render() {
    return await Hook.run("render", this, async () => {
      await this.session.canvas.update(this)
    })
  }

  async swalMessage(type, text) {
    await swal({icon: type, text: text})
  }

  async showNotification(type, text) {
    notify({message: text, type})
  }

  /**
   * Show message to user, get's overruled somewhere
   * @see {@link Canvas}
   * @param {string} type - message type(warning/succes/error/..)
   * @param {string} message - message text
   * @param {*} args - arguments
   * @returns {void | Object} Message output
   */
  message(type, message, args = null) {
    let simple = type === "warning" || type === "success"
    let event = {type, message, args, simple, window: this}

    return Hook.run(
      "message",
      this,
      () => {
        // Should not be reached
        //
      },
      event,
    )
  }

  /**
   * Focus self
   * @see {@link Session.activeWindow}
   * @returns {Promise} Promise
   */
  async focus() {
    // prevent if we are already the active window
    if (this.session.activeWindow && this.session.activeWindow.id === this.id) {
      return null
    }

    // hookable
    return await Hook.run("focus", this, () => {
      this.session.activeWindow = this
    })
  }

  /**
   * Go to page
   * @param {number} pageNo - Page number
   * @param {boolean} noReload - Should we reload?
   * @returns {void}
   */
  page(pageNo, noReload) {
    let event = {page: Number(pageNo)}

    return Hook.run(
      "paginate",
      this,
      async () => {
        if (event.page > 0) {
          this.input.Data.PageNumber = event.page

          if (noReload) {
            return
          }

          await this.reload(true)
        }
      },
      event,
    )
  }

  /**
   * Render window
   * @returns {string} HTML code
   */
  html() {
    $("body").addClass("avoid-clicks")
    let result = templater.renderWindow(this)
    $("body").removeClass("avoid-clicks")
    return result
  }

  /**
   * Set page size
   * @param {number} size - page size
   * @returns {Promise} Promise
   */
  async pagesize(size) {
    // get current values
    let currentSize = this.input.Data.PageSize
    let currentPage = this.input.Data.PageNumber

    // calculate offset and new page size
    let rowOffset = currentSize * currentPage
    let newPage = Math.floor(rowOffset / size)

    // set new values
    this.input.Data.PageSize = Number(size)
    this.input.Data.PageNumber = Math.max(newPage, 1)

    // get new values and re-render
    await this.fetch()
  }

  /**
   * Build criteria
   * @param {Array} rowsIndexes - Number indexes of key rows
   * @returns {Array} Criteria per row
   */
  buildCriteria(rowsIndexes) {
    // get criteria for each row index
    return rowsIndexes.map((index) => this.buildCriteriaNew(index))
  }
  /**
   * Set loading, get's proxied
   * @see {@link Canvas}
   * @param {boolean} isLoading - New value
   * @returns {Promise} Promise
   */
  async toggleLoading(isLoading) {
    await Hook.run("toggleLoading", this, () => {
      this.loading = isLoading
    })
  }

  /**
   * Open tab in parent
   * @param {*} tabId - Tab ID
   *  @returns {Promise} Promise to open tab in parent
   */
  async opentabparent(tabId) {
    // Do not open ourself
    if (this.parent.sub.Id === tabId) {
      return
    }

    // open tab
    await this.parent.opentab(tabId)
  }

  /**
   * Open tab in sub window
   * @param {*} tabId - Tab ID
   * @returns {Promise} Promise to open tab
   */
  async opentab(tabId) {
    // get tab
    let tab = this.tabs.filter((t) => t.Id === tabId).pop()

    // set request options
    let opts = {
      selectionInfo: {
        ActionName: tab.ActionName,
        Arguments: {},
        Request: this.output.Request,
        Data: {
          Selection: this.selection,
        },
      },
    }

    // Send request to server to open tab
    let result = await this.session.request(
      tab.WebService + "/" + tab.WebMethod,
      opts,
    )

    if (result !== null) {
      // set new selected tab
      this.tabs.forEach((x) => {
        x.Selected = tab === x
      })

      // Store ActionName in input.Data.SelectedTabActionName to keep selected tab after reset
      this.input.Data.SelectedTabActionName = tab.ActionName

      // Load and render sub window
      await this.injectSub(result)
      await this.sub.window.render()
    }
  }

  /**
   * Insert sub window or create new window
   * @param {Object} output - Window output
   * @param {boolean} inSub - Insert as sub window?
   * @param {boolean} noRender - should we render the window?
   * @param {boolean} noProcessing - Should we process the output?
   * @returns {Promise} Promise
   */
  async insertWindow(
    output,
    inSub = false,
    noRender = false,
    noProcessing = false,
  ) {
    let window = new Window(this.session, inSub ? this : null)
    window.input = output.Request

    // process data if neccessairy
    if (!noProcessing) {
      await window.process(output)
    }

    // we have done all the loading already
    window.loading = false

    // where should we place the new window
    if (inSub) {
      // into self
      this.sub = {
        window: window,
      }
    } else {
      // as new tab
      this.session.tabs.unshift(window)
      window.focus()
    }

    if (!noRender) {
      await window.render()

      if (window.output.Options && window.output.Options.HelpUrl) {
        drawVueManualModal({
          windowID: window.id,
          helpUrl: window.output.Options.HelpUrl,
        })
      }
    }
  }

  /**
   * Insert sub, get's proxied to insertWindow
   * @param {Object} output - Window output
   * @param {boolean} noRender - should we render the window?
   * @param {boolean} noProcessing - Should we process the output?
   * @returns {Promise} Promise
   */
  injectSub(output, noRender = false, noProcessing = false) {
    return this.insertWindow(output, true, noRender, noProcessing)
  }

  /**
   * Send updateWindow request to parent
   * @param {Object} output - New output object
   * @returns {Promise} Promise
   */
  async updateParentWindow(output) {
    this.parent.updateWindow(output)
  }

  /**
   * Update window with new output
   * @param {Object} output - New output object
   * @returns {Promise} Promise
   */
  async updateWindow(output) {
    // set loading (get's removed by process)
    await this.toggleLoading(true)

    // process and render
    await this.process(output)
    await this.toggleLoading(false)
    await this.render()
  }

  /**
   * Run action
   * @param {string} name - Action name
   * @param {string} targetTableName  - Name of target table
   * @param {string} webserviceUrl - Service URL
   * @param {string} webMethodName - Webmethod name
   * @param {boolean} doNotUseInternal - Should we use internal?
   * @returns {Promise} Promise
   */
  async action(
    name,
    targetTableName,
    webserviceUrl,
    webMethodName,
    doNotUseInternal,
  ) {
    let actionName = name.replace(/\|/g, ":")
    if (!doNotUseInternal) {
      this.lastActionArgs = [
        actionName,
        targetTableName,
        webserviceUrl,
        webMethodName,
        doNotUseInternal,
      ]
    }

    let button = this.output.Buttons.filter(
      (x) => x.ActionName == actionName,
    ).pop()
    let tab =
      button || this.output.Tabs.filter((x) => x.ActionName == actionName).pop()

    if (tab) {
      this.tabs.forEach((x) => {
        x.Selected = tab === x
      })

      // Store ActionName in input.Data.SelectedTabActionName to keep selected tab after reset
      if (this.output.Tabs.length > 0) {
        let selectedTab = this.output.Tabs.filter(
          (x) => x.ActionName == actionName,
        ).pop()
        this.input.Data.SelectedTabActionName =
          selectedTab != undefined ? selectedTab.ActionName : null
      }
    }

    let spec = button || tab

    let customFn = this["action" + actionName]

    if (typeof customFn === "function" && !doNotUseInternal) {
      let result = customFn.call(
        this,
        targetTableName,
        webserviceUrl,
        webMethodName,
        true,
      )
      await Promise.resolve(result)
      return
    }

    // Move primitive properties from customData into actionInputArgs
    if (this.customData) {
      for (var property in this.customData) {
        if (typeof this.customData[property] !== "object") {
          this.actionInputArgs[property] = this.customData[property]
        }
      }
    }

    let opts = {
      selectionInfo: {
        ActionName: actionName,
        Arguments: this.actionInputArgs || {},
        Request: this.output.Request,
        Data: {
          Selection: this.selection,
        },
      },
    }

    this.actionInputArgs = {}

    // if (spec != null && spec.SendData != null && spec.SendData.indexOf("Changes") >= 0) {
    if (this.output.Table != null) {
      let changes = []

      for (
        let rowIndex = 0;
        rowIndex < this.output.Table.Rows.length;
        rowIndex++
      ) {
        let formData = {}
        let criteria =
          this.output.Request.Prefix != "New"
            ? this.buildCriteriaNew(this.output.FullTable.Rows[rowIndex])
            : null

        for (let cell of this.output.FullTable.Rows[rowIndex]) {
          let col = cell.Column
          let cname = col.Name

          if (col.IsAutoNumber && this.output.Request.Prefix === "New") {
            formData[cname] = null
            continue
          }

          if (col.IsReadOnly) {
            continue
          }

          let formValue = cell.NewValue

          if (
            !cell.IsDirty &&
            this.output.Request.Prefix != "New" &&
            this.output.Request.Prefix != "Preview" &&
            this.output.Request.Prefix != "Multi"
          ) {
            continue
          }

          formData[cname] = formatter.serializeValue(col, formValue)
        }
        changes.push({Criteria: criteria, Values: formData})
      }

      opts.selectionInfo.Data.Changes = changes
    }

    if (
      spec != null &&
      spec.SendData != null &&
      spec.SendData.indexOf("Form") >= 0
    ) {
      let form = serializeForm(this.element)

      for (let x in form) {
        if (String(form[x]).length < 1) {
          delete form[x]
          continue
        }
      }

      if (this.output.Data != null && this.output.Data.Type == "tiles") {
        delete form.scan
      }

      opts.selectionInfo.Data.Form = form
    }

    let result
    this.toggleLoading(true)
    try {
      result = await this.session.request(
        (webserviceUrl || "/Admin/WebServices/CoreWebServices.asmx") +
          "/" +
          webMethodName,
        opts,
      )
    } catch (error) {
      Sentry.captureException({username: global.session.username, ...error, config: {...error.config, data: JSON.stringify(error.config.data), headers: JSON.stringify(error.config.headers), params: JSON.stringify(error.config.params)}, window: {Subject: global.session.activeWindow.output.Request.Subject, Criteria: global.session.activeWindow.output.Request.Criteria}})
      let errorMessage
      if (error.message != undefined) {
        errorMessage = error.message
      } else if (error.data.Error != undefined) {
        errorMessage = error.data.Error
      }

      this.message("error", errorMessage)
      console.error(error)
      return
    }

    this.actionInputArgs = {}
    await this.handleActionResponse(result)
  }

  /**
   * Build criteria from primary keys of row
   * @param {Object} row - Row object
   * @returns {Object} criteria
   */
  buildCriteriaNew(row) {
    let criteria = {}

    for (let cell of row) {
      if (cell.Column.IsPrimaryKey) {
        criteria[cell.Column.Name] = cell.Value

        if (cell.Column.Type == "Date") {
          criteria[cell.Column.Name] = formatter.serializers.Date(cell.Value)
        } else if (cell.Column.Type == "DateTime") {
          criteria[cell.Column.Name] = formatter.serializers.DateTime(
            cell.Value,
          )
        }
      }
    }

    return criteria
  }

  /**
   * Inject Window, proxy of updateWindow
   * @see {@link Window.updateWindow}
   * @param {Window} window - Window to inject
   * @return {Promise} Promise to update window
   */
  injectWindow(window) {
    return this.updateWindow(window)
  }

  /**
   * Hanlde action response
   * @param {Object} response - Action response
   * @returns {Promise} Promse to handle action
   */
  async handleActionResponse(response) {
    if (!response || !response.IsActionResponse) {
      console.error("Not an action response:", response)
      // throw new Error("Type mismatch, expected an action response.")
      return
    }

    for (let eventArgs of response.Events) {
      await this.event(...eventArgs)
    }
  }

  /**
   * Export table to excel
   * @param {string} targetTableName - Action target table
   * @param {string} webserviceUrl - Service URL
   * @param {string} webMethodName - Method name
   * @returns {Promise} Promise
   */
  async actionExcel() {
    let downloadLink = "/Admin/Pages/ExportToExcel.aspx"

    let params = this.session.comboboxes.selectedValues
    if (params == null) {
      params = {}
    }

    for (let key in params) {
      if (downloadLink.indexOf("?") == -1) {
        downloadLink = downloadLink + "?" + key + "=" + params[key]
      } else {
        downloadLink = downloadLink + "&" + key + "=" + params[key]
      }
    }

    let context = {
      ActionName: "Excel",
      Request: this.input,
      Arguments: {},
      Data: {},
    }

    await download(downloadLink, {
      context,
    })
  }

  async actionDownLoadTemplate() {
    let downloadLink = "/Admin/Pages/DownloadTemplate.aspx"

    let params = this.session.comboboxes.selectedValues
    if (params == null) {
      params = {}
    }

    for (let key in params) {
      if (downloadLink.indexOf("?") == -1) {
        downloadLink = downloadLink + "?" + key + "=" + params[key]
      } else {
        downloadLink = downloadLink + "&" + key + "=" + params[key]
      }
    }

    let context = {
      ActionName: "Excel",
      Request: this.input,
      Arguments: {},
      Data: {},
    }

    await download(downloadLink, {
      context,
    })
  }

  async actionOpenExcelFileInput() {
    let window = this
    document.getElementById("ExcelFileCatcher-" + window.id).oninput =
      function () {
        window.actionImportExcelFile(this)
      }
  }

  async actionImportExcelFile(element) {
    let window = this
    // let file = element.files[0];
    // let extension = file.substr((file.lastIndexOf('.') +1));
    let url =
      "/Admin/Pages/ImportFromExcel.aspx?subject=" +
      encodeURIComponent(window.output.Request.Subject)

    for (const [key, value] of Object.entries(
      window.output.Request.Criteria[0],
    )) {
      url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    }

    let filename = element.files[0].name
    let extension = filename.split(".")[1]
    let allowed_extensions = new Array("xls", "xlsx", "docb", "ods")

    if (
      filename &&
      filename.length > 0 &&
      allowed_extensions.includes(extension)
    ) {
      let data = new FormData()
      let file = element.files[0]
      data.append("file" + 0, file)
      $.ajax({
        type: "POST",
        url: url,
        contentType: false,
        processData: false,
        data: data,
        success: function (result) {
          if (result.Error) {
            let dbError = result.Error.Message
            let friendlyUserError = dbError.substring(dbError.indexOf(":") + 1)
            window.swalMessage("warning", friendlyUserError)
          } else {
            window.handleActionResponse(result)
          }
        },
        error: function (error) {
          // alert('error!')
        },
      })
      this.reload()
      document.getElementById("ExcelFileCatcher-" + window.id).oninput =
        function () {
          window.actionImportExcelFile(this)
        }
    } else {
      window.swalMessage("warning", this.session.translations.InvalidExcelFile)
    }
  }

  /**
   * Set bulkedit
   * @param {string} targetTableName - Action target table
   * @param {string} webserviceUrl - Service URL
   * @param {string} webMethodName - Method name
   * @returns {Promise} Promise
   */
  async actionBulk(targetTableName, webserviceUrl, webMethodName) {
    this.bulkedit = targetTableName

    this.toggleLoading(true)
    await this.render()
    this.toggleLoading(false)
  }

  /**
   * Cancel bulk edit
   * @returns {Promise} Promise
   */
  async cancelBulk() {
    if (
      this.selection.length &&
      !confirm(this.session.translations.WindowWarningOnClose)
    ) {
      return
    }

    this.bulkedit = false
    await this.render()
  }

  /**
   * Save bulk edit
   * @returns {Promise} Promise
   */
  async saveBulk() {
    let _this = this
    let uniChanges = []

    // If we aren't in bulk mode, maybe our parent or sub are
    if (!this.bulkedit) {
      if (this.sub && this.sub.window && this.sub.window.bulkedit) {
        await this.sub.window.saveBulk()
      }

      if (this.parent && this.parent.bulkedit) {
        await this.parent.saveBulk()
      }

      return
    }

    // get all rows
    let $tableRows = $(this.element).find(".table-row").toArray()

    // construct data and criteria for each row
    for (let tableRowEl of $tableRows) {
      let data = {}
      let criteria = null
      let rowData = this.output.Data.Rows[$(tableRowEl).attr("data-row-index")]

      if (rowData != null) {
        criteria = {}
        // construct criteria
        for (let columnName in this.output.Data.Columns) {
          let column = this.output.Data.Columns[columnName]

          if (column.IsPrimaryKey) {
            criteria[columnName] = rowData[columnName]
          }
        }
      }

      // Get all values, sanitized
      $(tableRowEl)
        .find("[name]")
        .each(function forEachName() {
          let name = $(this).attr("name").split(/\[|\]/g).shift()
          let value = $(this).is("[contenteditable]")
            ? $(this).text()
            : $(this).val()

          let col =
            _this.output.Table.Rows[$(tableRowEl).attr("data-row-index")].Column
          if (!col) {
            return
          }

          value = formatter.serializeValue(col, value)
          data[name] = value
        })

      let newValues = {}

      for (let columnName in data) {
        if (rowData == null || data[columnName] !== rowData[columnName]) {
          newValues[columnName] = data[columnName]
        }
      }

      let commitChanges = $(tableRowEl)
        .find("[data-commit-checkbox]")
        .prop("checked")

      if (commitChanges) {
        uniChanges.push({Criteria: criteria, Values: newValues})
      }
    }

    let opts = {
      changes: uniChanges,
      tableName: this.bulkedit,
    }

    await Hook.run("saveBulk", this, async () => {
      // save values
      let save = await this.session.request(
        "/Admin/WebServices/CoreWebServices.asmx/SaveBulk",
        opts,
      )

      this.bulkedit = false
      this.session.updateRelatedWindows(this)
      await this.reload()
    })
  }

  /**
   * Alias for dispose
   * @see {dispose}
   * @returns {void}
   */
  close() {
    return this.dispose()
  }

  /**
   * Action to download file
   * @returns {Promise} Promise
   */
  async actionDownloadFile() {
    let url =
      "/Admin/Pages/DownloadFile.aspx?tableName=" +
      encodeURIComponent(this.output.Request.Subject)
    let queue = []

    for (let rowCriteria of this.selection) {
      url = url + "&" + $.param(rowCriteria)
    }

    queue.push(download(url))
    await Promise.all(queue)
  }

  /**
   * Action to upload file
   * @param {Array} droppedFiles - Array of files
   * @returns {Promise} Promise
   */
  async actionUploadFile(droppedFiles, primaryKey) {
    let files
    if (droppedFiles) {
      files = droppedFiles
    } else {
      files = $("[name='FileUpload']").prop("files")
    }

    if (!files || !files.length) {
      return
    }

    let data = new FormData()

    for (let f of files) {
      data.append(f.name, f)
    }

    let requestUrl = this.output.Options.UploadUrl

    if (this.output.Data.Type === "quickeditform") {
      requestUrl = requestUrl.replace(/\?.*/, "")
      requestUrl =
        requestUrl + "?tableName=" + this.output.Data.UploadFullTableName
    }

    let result

    this.toggleLoading(true)
    try {
      if (primaryKey) {
        result = await this.session.request(requestUrl, data, primaryKey)
      } else {
        result = await this.session.request(requestUrl, data, {})
      }

      this.handleActionResponse(result)
      this.handleActionResponse(result)
    } finally {
      this.toggleLoading(false)
    }
  }

  /**
   * Open window based on given values
   * @deprecated
   * @param {string} tableName - Table name
   * @param {string} primaryKeyName - Name of primary key column
   * @param {string} primaryKeyValue - Value of primary key column
   * @param {Array} extraParams - Extra parameters that have to be copied
   * @returns {Promise} To create window
   */
  async open(tableName, primaryKeyName, primaryKeyValue, extraParams) {
    let params = {
      Subject: tableName,
      Prefix: "Multi",
    }

    if (primaryKeyName != null) {
      params.Criteria = [{[primaryKeyName]: primaryKeyValue}]
      params.Prefix = "Single"
    }

    if (extraParams != null) {
      for (let key in extraParams) {
        params[key] = extraParams[key]
      }
    }

    await this.session.openWindow(params)
  }

  /**
   * Create window based on criteria
   * @param {string} tableName - Table name
   * @param {Object} rowCriteria - Row critiria object
   * @deprecated
   * @returns {Promise} To create window
   */
  async openCriteria(tableName, rowCriteria) {
    $("body").addClass("avoid-clicks")
    await this.session.openWindow({
      Subject: tableName,
      Prefix: "Single",
      Criteria: [rowCriteria],
    })
    $("body").removeClass("avoid-clicks")
  }

  /**
   * Remove text query
   * @param {*} id - Search id
   * @returns {Promise} To re-render
   */
  async removeTextQuery(id) {
    if (!this.input.Data.Search.length) {
      return
    }

    // let textFilter = this.input.Data.Search[id]
    this.input.Data.Search.splice(id, 1)
    await this.reload()
  }

  /**
   * Remove Date filter
   * @param {*} id - Date filter id
   * @returns {Promise} To re-render
   */
  async removeDateQuery(id) {
    //
    let otherFilter = this.filters.date[id]
    let col =
      typeof this.filters.usedate == "string"
        ? this.filters.usedate
        : "DateTimeCreated"

    // If we set a custom search column set it here rather than the hardcoded DateTimeCreated
    if (this.output.Filters) {
      for (let filter of this.output.Filters) {
        if (filter.Type === "Date") {
          col = filter.Target
        }
      }
    }
    delete this.input.Data.Filters[col + " " + otherFilter.suffix]
    otherFilter.value = null
    await this.reload()
  }

  /**
   * @deprecated
   * @todo what is this?
   * @param {string} name - Name
   * @param {Array} args - Array of arguments
   * @returns {Promise} Nothing
   */
  async filterAction(name, ...args) {
    //
  }

  /**
   * Open from tree
   * @param {*} id - Tree id
   * @returns {Promise} Promise
   */
  async opentree(id) {
    if (!id) {
      return
    }
    // find selected element and set 'selected'
    $(this.element).find("[data-window-tree-id]").removeClass("selected")
    $(this.element)
      .find("[data-window-tree-id='" + id + "']")
      .addClass("selected")

    // Criteria is TableName + ID (f.e. CategoryID)
    let criteria = this.output.Request.Subject.split(".")[1] + "ID"
    let input = {
      Subject: this.output.Request.Subject,
      Prefix: "Single",
      Criteria: [{[criteria]: id}],
    }

    // reload sub
    if (this.sub !== null) {
      this.sub.window.empty = false
      this.sub.window.input = input
      this.sub.window.reload()
      return
    }
  }

  /**
   * Open reference
   * @param {string} columnName - Name of column
   * @param {*} value - Value of cell
   * @returns {Promise} To create window
   */
  async openref(columnName, value) {
    let col = this.output.Table.Columns.filter(
      (c) => c.Name === columnName,
    ).pop()

    if (!col) {
      col = this.customData?.Columns.filter((c) => c.Name === columnName).pop()
    }

    if (!col) {
      col = this.customData.Comboboxes[columnName]?.specification
    }

    if (col == null) {
      return
    }

    let foreign = col.Dropdown ?? col

    // construct input
    let input = {}
    input.Subject = foreign.TableName ?? foreign.tableName
    input.Prefix = "Multi"
    input.Criteria = [{}]

    // Go to foreign field of a value is selected,
    // otherwise, go to table
    if (value) {
      input.Prefix = "Single"
      input.Criteria[0][foreign.ColumnName ?? foreign.columnName] = value
    }

    // Add extra keys to criteria
    if (
      foreign.ExtraKeys != null &&
      this.output.Data != null &&
      this.output.Data.Rows != null &&
      this.output.Data.Rows[0] != null
    ) {
      let currentDataRow = this.output.FullTable.Rows[0]
      for (let primaryKey of foreign.ExtraKeys) {
        let filteredRow = currentDataRow.filter(
          (x) => x.Column.Name == primaryKey,
        )
        if (filteredRow.length > 0) {
          let primaryKeyCell = filteredRow[0]
          input.Criteria[0][primaryKey] = primaryKeyCell.IsDirty
            ? primaryKeyCell.NewValue
            : primaryKeyCell.Value
        }
      }
    }

    // create window
    await this.session.openWindow(input)
  }

  /**
   * Resize window
   * @returns {Promise} Promise
   */
  async resize() {
    await Hook.run("resize", this)
  }

  /**
   * Clear html thoroughly
   * @returns {void}
   */
  clearHTML() {
    if (!this.element) {
      return
    }

    if (this.element.firstChild) {
      while (this.element.children.length > 0) {
        this.element.removeChild(this.element.children[0])
      }
    }

    this.element.innerHTML = ""
  }

  /**
   * Toggle if column should be visible
   * @param {string} columnName - Name of column
   * @returns {Promise} Promise
   */
  async toggleColumn(columnName) {
    let index = this.output.Data.HiddenColumns.indexOf(columnName)
    let columnsToHide = this.output.Data.HiddenColumns
    if (index === -1) {
      columnsToHide.push(columnName)
    } else {
      columnsToHide.splice(index, 1)
    }
    // let result = await this.session.request("/Admin/WebServices/CoreWebServices.asmx/SaveHiddenColumns", {
    // 	subject: this.output.Request.Subject,
    // 	columnNames: columnsToHide,
    // 	prefix: this.output.Request.Prefix
    // })

    await this.handleActionResponse(result)
  }

  /**
   * Get selected filter item
   * @deprecated
   * @param {*} filter - Filter
   * @returns {*} Selected
   */
  selectedFilterItem(filter) {
    this.input.liDiFilterOptions = this.input.liDiFilterOptions || []
    let selected = this.input.liDiFilterOptions
      .filter((f) => filter.list.filter((opt) => equal(f, opt.value)).length)
      .pop()

    if (!selected && filter.suffix) {
      selected = this.input.liDiFilterOptions
        .filter(
          (f) =>
            filter.list.filter((opt) => {
              let arg = $.extend({}, opt.value)

              for (let prop in arg) {
                if (arg.hasOwnProperty(prop)) {
                  arg[prop] = arg[prop] + " " + filter.suffix
                }
              }

              return equal(f, arg)
            }).length,
        )
        .pop()
    }

    return selected
  }

  /**
   * Process a change in value
   * @param {number} row - Row id
   * @param {number} index - Column index
   * @param {*} val - Cell value
   * @returns {*} Returns cell if there was a change relative to the value in the database
   */
  processChange(row, index, val) {
    let isNew = this.output.Request.Prefix == "New"

    if (this.output.Table.Rows.length < row) {
      return null
    }

    let cell = this.output.Table.Rows[row][index]
    if (!cell) {
      return null
    }
    // val = formatter.parseValue(cell.Column, val)

    // Value as it came from the server.
    let serverValue = (cell.Initial && cell.Initial.Value) || cell.Value
    let triggerChange =
      cell.NewValue != val && !(cell.Value === null && val === "")

    // Dirty if: not equal the server value AND not equal the last new value.
    cell.IsDirty = serverValue != val
    cell.NewValue = val

    if (triggerChange) {
      //

      if (cell.Column.IsPostback) {
        let values = {}

        for (let rowcell of this.output.FullTable.Rows[row]) {
          values[rowcell.Column.Name] = rowcell.NewValue
        }

        Promise.resolve(this.postbackValues(values))
      }
    }

    return triggerChange && !isNew ? cell : null
  }

  /**
   * Send postback to server
   * @param {Object} values - Row values
   * @returns {Promise} Promise
   */
  async postbackValues(values) {
    let previousValues = this.previousPostbackValues
    this.previousPostbackValues = values

    let result = await this.session.request(
      "/Admin/WebServices/CoreWebServices.asmx/PostbackValues",
      {
        request: this.input,
        values: values,
        previousValues: previousValues,
      },
    )

    await this.handleActionResponse(result)
  }

  /**
   * Apply values to window
   * @param {*} newValues - New values
   * @returns {void}
   */
  applyChanges(newValues) {
    let inputElements = $(this.element).find("[name]").toArray()
    let inputs = {}
    for (let inputElement of inputElements) {
      let name = $(inputElement).attr("name")
      inputs[name] = inputElement
    }

    let row = this.output.Table.Rows[0]

    for (let columnName in newValues) {
      let cell = row[columnName]

      if (cell != null) {
        cell.NewValue = newValues[columnName]
      }

      let inputElement = inputs[columnName]

      if (inputElement != null) {
        let serializedValue =
          cell != null
            ? formatter.parseValue(cell.Column, newValues[columnName])
            : newValues[columnName]

        if ($(inputElement).is("[data-html-editor]")) {
          $(inputElement).html(serializedValue)
        } else if ($(inputElement).is("[contenteditable]")) {
          $(inputElement).text(serializedValue)
        } else if ($(inputElement).is("input[type=checkbox]")) {
          $(inputElement).prop("checked", serializedValue)
        } else {
          $(inputElement).val(serializedValue)
        }

        $(inputElement).change()
      }
    }
  }

  /**
   * Check if dirty values exists
   * @returns {boolean} True if the window is dirty
   */
  isDirty() {
    if (this.bulkedit) {
      return true
    }

    if (this.output != null && this.output.Table != null) {
      for (let row of this.output.Table.Rows) {
        if (row.filter((cell) => cell.IsDirty).length > 0) {
          return true
        }
      }
    }

    return false
  }
  /**
   * Checks if it is related to the given tablename
   * @param {string} tablename - Table name
   * @returns {boolean} True if related
   */
  isRelated(tablename) {
    if (!tablename || (this.parent && this.parent.waitingForReload)) {
      return false
    }

    return (
      (typeof tablename === "string" &&
        this.output.Request.Subject === tablename) ||
      (typeof tablename === "object" &&
        tablename.indexof(this.output.Request.Subject) > -1) ||
      (typeof tablename === "string" &&
        this.output.Request.LinkedSubject &&
        this.output.Request.LinkedSubject === tablename) ||
      (typeof tablename === "object" &&
        this.output.Request.LinkedSubject &&
        tablename.indexof(this.output.Request.LinkedSubject) > -1)
    )
  }

  /**
   * Select a row
   * @param {number} rowIndex - Index of row
   * @param {boolean} checked - If the row is selected
   * @returns {void}
   */
  selectRow(rowIndex, checked) {
    if (
      this.output == null ||
      this.output.Data == null ||
      (this.output.Data.Type != "table" &&
        this.output.Data.Type != "upload" &&
        this.output.Data.Type != "quickeditform")
    ) {
      return
    }

    // Get criteria
    let primaryKeys = this.buildCriteriaNew(
      this.output.FullTable.Rows[rowIndex],
    )

    // Ignore if all primaryKeys are null
    if (Object.values(primaryKeys).every((x) => x === null)) {
      return
    }

    if (checked) {
      if (
        this.selection.filter((existing) => equal(existing, primaryKeys))
          .length == 0
      ) {
        // append criteria
        this.selection.push(primaryKeys)
      }
    } else {
      // remove critiria
      this.selection = this.selection.filter(
        (existing) => !equal(existing, primaryKeys),
      )
    }
  }

  /**
   * Checks if the given row is selected
   * @param {number} rowIndex - Row number
   * @returns {boolean} True if the row is selected
   */
  isSelectedRow(rowIndex) {
    // We do not store the row indexes, so this way you can still check if the row is selected
    // room for improvement
    let primaryKeys = this.buildCriteriaNew(
      this.output.FullTable.Rows[rowIndex],
    )
    return (
      this.selection.filter((existing) => equal(existing, primaryKeys)).length >
      0
    )
  }

  /**
   * Reverse column sorting
   * @param {string} column - Column name
   * @returns {Promise} Promise to reload
   */
  async toggleColumnSorting(column) {
    if (this.bulkedit) {
      return
    }

    let columnSpec = this.output.FullTable.Columns.filter(
      (x) => x.Name == column,
    ).pop()

    if (
      columnSpec == null ||
      !columnSpec.CanSortOn ||
      (columnSpec.Dropdown || {}).Items != null
    ) {
      return
    }

    this.input.Data.SortReverse =
      this.input.Data.SortOn == column ? !this.input.Data.SortReverse : false
    this.input.Data.SortOn = column

    await this.reload()
  }

  /**
   * Alias for download
   * @param {string} url - Download URL
   * @param {Object} opts - Download options
   * * @param {Object} method - Post or Get
   * @returns {Promise} Promise to download
   */
  async download(url, opts, method) {
    await download(url, opts, method)
  }

  /**
   * Random function, why does it exist?
   * @deprecated
   * @returns {number} Math.random()
   */
  random() {
    return Math.random()
  }

  /**
   * Select permissions for table
   * @todo fill roleID, possilbly depricated
   * @param {string} tableName - Table name
   * @param {*} roleID - Role ID
   * @param {string} breadcrumbs - breadcrums
   * @returns {Promise} Promise
   */
  async selectPermissions(tableName, roleID, breadcrumbs = "") {
    let url =
      "/Admin/WebServices/MembershipWebServices.asmx/GetRolePermissionData"
    let input = {
      tableName: tableName,
      roleID: roleID,
    }
    // Fixed strange browser bug, uncheck all checkboxes before reloading data.
    // Firefox & Chrome are remembering Checked action buttons.
    $("input:checkbox", window.element).removeAttr("checked")

    let output = await this.session.request(url, input)
    output.FormPermissionMenu = arrayToTree(output.FormPermissionMenu, {
      parentProperty: "ParentID",
      customID: "ID",
    })

    output.inputVars = input
    output.breadCrumbs = [].concat(breadcrumbs.split(/;/g))

    this.setupCustomEnv("permissions", output, true)
  }

  /**
   * Select permissions for table for sub
   * @todo fill roleID, possilbly depricated
   * @param {string} tableName - Table name
   * @param {*} roleID - Role ID
   * @param {string} breadcrumbs - breadcrums
   * @returns {Promise} Promise
   */
  async selectPermissionsSub(tableName, roleID, breadcrumbs = "") {
    this.injectSub({}, true, true)
    this.sub.window.selectPermissions(tableName, roleID, breadcrumbs)
  }

  /**
   * Save permissions
   * @param {string} tableName - Table name
   * @param {*} roleID - Role id
   * @returns {Promise} Promise
   */
  async savePermissions(tableName, roleID) {
    let permissionInfo = {
      tableName: tableName,
      roleID: roleID,
    }

    ;["FormPermissions", "Actions", "Columns", "Tabs"].forEach((i) => {
      let values = serializeForm(
        $(this.element)
          .find("[data-form-id=" + i + "]")
          .get(0),
      )

      let formattedNeed = false
      let formatted = {}

      for (let j in values) {
        let bracketText = (j.match(/\[([^)]+)\]/) || [null, null])[1] || null

        // let val = values[i] === "on"
        let val = values[j]
        if (bracketText) {
          formattedNeed = true
          let colName = j.substr(0, j.length - bracketText.length - 2)
          formatted[colName] = formatted[colName] || {}
          formatted[colName][bracketText] = val
        } else {
          values[j] = val
        }
      }

      permissionInfo[i] = formattedNeed ? formatted : values || {}
    })

    let url =
      "/Admin/WebServices/MembershipWebServices.asmx/SetRolePermissionData"
    let output = await this.session.request(url, {permissionInfo})

    this.handleActionResponse(output)
  }

  /**
   * Save checked (and unchecked) values from treeview
   */
  async saveTreeView() {
    let initialValues = this.output.Data.initialValues
    let values = serializeForm(this.element)

    // remove values that are the same in the currentlist and in the initial list
    for (let property in initialValues) {
      if (
        initialValues.hasOwnProperty(property) &&
        values.hasOwnProperty(property) &&
        initialValues[property] === values[property]
      ) {
        delete values[property]
      }
    }

    let data = {
      subject: this.output.Request.LinkedSubject,
      criteria:
        this.output.Request.Criteria.length > 0
          ? this.output.Request.Criteria[0]
          : null,
      values: values,
    }

    let output = await this.session.request(
      "/Admin/WebServices/CoreWebServices.asmx/SaveTree",
      data,
    )

    this.handleActionResponse(output)
  }

  /**
   * Cancel tree, alias for reset
   * @see {@link Window.reset}
   * @returns {Promise} Promise
   */
  async cancelTreeView() {
    await this.reset()
  }

  /**
   * Set custum values
   * @param {string} templateName - Custom template name
   * @param {Object} customData - Custom data
   * @param {boolean} keepInput - Keep input
   * @returns {void}
   */
  setupCustomEnv(templateName, customData, keepInput) {
    this.customTemplateName = templateName
    this.customData = customData
    this.input = keepInput ? this.input : null
    this.render()
    // // find all elements with title attribute
    // let elementsTooltip = document.querySelectorAll("[title]")

    // // attach a tooltip for each
    // for (let i = 0; i < elementsTooltip.length; i++) {
    // 	new bootstrap.Tooltip(elementsTooltip[i], {
    // 		placement: "top", //string
    // 		animation: "slideNfade", // CSS class
    // 		delay: 150, // integer
    // 	})
    // }
  }

  /**
   * Open dialog with user
   * @param {string} instructionText - Instruction text displayed in message
   * @param {Array} fields - Fields to show in dialog
   * @param {boolean} isStack - If is stack
   * @returns {Promise} Promise
   */
  async dialog(instructionText, fields = null, isStack) {
    if (fields) {
      fields.forEach((f) => {
        if (f.Initial != null && f.Initial.Value != null) {
          f.Initial.Value = formatter.parseValue(f, f.Initial.Value)
        }

        if (f.DropdownItems) {
          f.Combobox = new Combobox()
          f.Combobox.populate(f.DropdownItems)
          f.Combobox.specification.name = f.Name
          if (
            f.Initial != null &&
            f.Initial.Value != null &&
            f.Initial.Text != null
          ) {
            f.Combobox.setInitialValues(f.Initial.Text, f.Initial.Value)
          }
        } else if (f.Dropdown) {
          f.Combobox = Combobox.new(null, {
            name: f.Dropdown.ColumnName,
            tableName: f.Dropdown.TableName,
            columnName: f.Dropdown.ColumnName,
          })
        }
      })
    }

    this.actionInputArgs = this.actionInputArgs || {}
    let formMessage = await this.message("dialog", instructionText, {fields})
    let form = formMessage.form

    if (form == null) {
      this.lastActionArgs = null
      this.actionInputArgs = {}
      return
    }

    for (let key in form) {
      let col = fields.filter((x) => x.Name == key)[0]
      this.actionInputArgs[key] = formatter.serializeValue(col, form[key])
    }

    if (!isStack && this.lastActionArgs) {
      this.toggleLoading(true)
      await this.action(...this.lastActionArgs)
      this.toggleLoading(false)
    }
  }

  /**
   * Open category in new window
   * @param {*} categoryID - Category ID
   * @deprecated
   * @returns {Promise} Promise
   */
  async openCategory(categoryID) {
    // This wasn't defined..?
    let newInput = {}
    newInput.Criteria = [{CategoryID: categoryID}]
    await this.session.openWindow(newInput)
  }

  /**
   * Opens help window
   * @returns {void}
   */
  help() {
    if (!this.output || !this.output.Options || !this.output.Options.HelpUrl) {
      this.message("warning", this.session.translations.NoHelpAvailable)
      return
    }

    global.window.open(
      this.output.Options.HelpUrl.replace(
        "[Session.productDomain]",
        this.session.productDomain,
      ),
    )
  }

  /**
   * Opens url in new tab in browser
   * @param {string} url - URL
   * @returns {void}
   */
  async openUrl(url) {
    if (url == null) {
      this.message("warning", this.session.translations.NoHelpAvailable)
      return
    }

    global.window.open(url)
  }

  /**
   * Reloads the complete web page with plain javascript reload() command.
   */
  async reloadWebpage() {
    location.reload()
  }

  /**
   * Get row css. Render function
   * @param {number} rowIndex - Row index
   * @returns {string} CSS information
   */
  getCssInfo(rowIndex) {
    let rowMeta = ((this.output.Data || {}).Metadata[rowIndex] || {})["*"] || {}

    let bg = rowMeta.BackgroundColor
    let fc = rowMeta.Color

    return (
      (bg != null ? "background-color: " + bg + ";" : "") +
      (fc != null ? "color: " + fc + ";" : "")
    )
  }

  /**
   * Get Columns
   * @returns {Array} List of searchable Columns
   */
  getSearchColumns() {
    return (
      (this.output &&
        this.output.Table &&
        this.output.Table.Columns.filter(
          (x) =>
            x.CanSortOn &&
            ["String", "Int", "Decimal", "Money", "DateTime", "Date"].indexOf(
              x.Type,
            ) >= 0,
        )) ||
      []
    )
  }
}
